import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { ArrowLeft, Trophy, MapPin, Clock, Users } from '@phosphor-icons/react';
import GamePill from '../components/GamePill';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GAME_TYPES = [
  'D&D', 'Chess', 'Catan', 'Magic: The Gathering',
  'Pokemon', 'Yu-Gi-Oh!', 'Warhammer', 'Monopoly',
  'Ticket to Ride', 'Scrabble', 'Risk', 'Other'
];

export default function CreateTournamentPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    game_type: '',
    description: '',
    date: '',
    time: '',
    location: '',
    max_players: 8,
    image: ''
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.game_type) {
      toast.error('Please select a game type');
      return;
    }

    setLoading(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/tournaments`, formData, { withCredentials: true });
      toast.success('Tournament created!');
      navigate(`/tournaments/${data.id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create tournament');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="app-container" data-testid="create-tournament-page">
      {/* Header */}
      <header className="p-6 pb-4 flex items-center gap-4">
        <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-full bg-[#181824] flex items-center justify-center">
          <ArrowLeft size={20} weight="bold" className="text-white" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-white" style={{ fontFamily: 'Unbounded, sans-serif' }}>
            <Trophy size={20} weight="duotone" className="inline mr-2 text-[#FFD700]" />
            Create Tournament
          </h1>
          <p className="text-[#71717A] text-sm">Organize a competitive event</p>
        </div>
      </header>

      {/* Form */}
      <form onSubmit={handleSubmit} className="px-6 pb-32 space-y-5" data-testid="create-tournament-form">
        {/* Game Type */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-3">Game *</label>
          <div className="flex flex-wrap gap-2">
            {GAME_TYPES.map((game) => (
              <GamePill
                key={game}
                game={game}
                selected={formData.game_type === game}
                onClick={() => setFormData(prev => ({ ...prev, game_type: game }))}
              />
            ))}
          </div>
        </div>

        {/* Name */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Tournament Name *</label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Weekly Chess Championship"
            required
            data-testid="tournament-name-input"
          />
        </div>

        {/* Description */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Description *</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            className="input-field min-h-[100px] resize-none"
            placeholder="Tournament rules, prizes, etc..."
            required
            data-testid="tournament-description-input"
          />
        </div>

        {/* Date & Time */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
              <Clock size={14} className="inline mr-1" /> Date *
            </label>
            <input
              type="date"
              name="date"
              value={formData.date}
              onChange={handleChange}
              className="input-field"
              required
              data-testid="tournament-date-input"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-[#A1A1AA] mb-2">Time *</label>
            <input
              type="time"
              name="time"
              value={formData.time}
              onChange={handleChange}
              className="input-field"
              required
              data-testid="tournament-time-input"
            />
          </div>
        </div>

        {/* Location */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
            <MapPin size={14} className="inline mr-1" /> Location *
          </label>
          <input
            type="text"
            name="location"
            value={formData.location}
            onChange={handleChange}
            className="input-field"
            placeholder="e.g., Game Shop Downtown"
            required
            data-testid="tournament-location-input"
          />
        </div>

        {/* Max Players */}
        <div>
          <label className="block text-sm font-medium text-[#A1A1AA] mb-2">
            <Users size={14} className="inline mr-1" /> Max Players *
          </label>
          <div className="flex gap-2">
            {[4, 8, 16, 32].map((num) => (
              <button
                key={num}
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, max_players: num }))}
                className={`flex-1 py-2 rounded-xl font-medium text-sm transition-all ${
                  formData.max_players === num
                    ? 'bg-gradient-to-br from-[#9D4CDD] to-[#7C3AED] text-white'
                    : 'bg-[#181824] text-[#A1A1AA]'
                }`}
              >
                {num}
              </button>
            ))}
          </div>
        </div>

        {/* Submit */}
        <motion.button
          type="submit"
          disabled={loading}
          className="btn-primary w-full mt-6"
          whileTap={{ scale: 0.98 }}
          data-testid="create-tournament-submit"
        >
          {loading ? (
            <span className="flex items-center justify-center gap-2">
              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              Creating...
            </span>
          ) : (
            'Create Tournament'
          )}
        </motion.button>
      </form>
    </div>
  );
}
